import React from 'react';
import { Lightning, Gauge, Robot, User } from '@phosphor-icons/react';
import { getCategoryColor, getCategoryLabel } from 'config/ideaCategories';

export default function IdeaSlide({ sectionNumber, idea, ideaIndex, totalIdeasInCategory }) {
    const color = getCategoryColor(idea.category);
    const ease = idea.ease || (idea.difficulty ? 10 - idea.difficulty + 1 : 5);
    const isAIGenerated = idea.source !== 'manual';

    const getScoreLabel = (value) => {
        if (value <= 3) return 'Basso';
        if (value <= 6) return 'Medio';
        return 'Alto';
    };

    return (
        <div className="h-full w-full bg-[#d6d6d6] p-8 md:p-16 overflow-y-auto relative">
            {/* Logo */}
            <div className="absolute top-8 right-8 md:top-16 md:right-16">
                <img
                    src="/images/opificioai-logo.png"
                    alt="OpificioAI"
                    className="h-6 md:h-8 w-auto opacity-60"
                />
            </div>

            <div className="max-w-4xl mx-auto">
                {/* Section Number */}
                <div className="text-[80px] md:text-[120px] font-bold text-black/10 leading-none mb-4">
                    {sectionNumber}
                </div>

                {/* Category Badge */}
                <div className="flex items-center gap-3 mb-6">
                    <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: color }}
                    />
                    <span className="text-sm font-medium text-black/50 uppercase tracking-wider">
                        {getCategoryLabel(idea.category)}
                    </span>
                    {totalIdeasInCategory > 1 && (
                        <span className="text-sm text-black/30">
                            ({ideaIndex + 1} di {totalIdeasInCategory})
                        </span>
                    )}
                </div>

                {/* Idea Title */}
                <h2 className="text-3xl md:text-5xl font-bold text-black/90 mb-8 leading-tight">
                    {idea.title}
                </h2>

                {/* Main Card */}
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    {/* Scores */}
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            {/* Impact */}
                            <div className="bg-black/[0.02] rounded-xl p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <Lightning size={24} weight="fill" className="text-amber-500" />
                                    <span className="text-lg font-medium text-black/70">
                                        Impatto Aziendale
                                    </span>
                                </div>
                                <div className="flex items-end gap-3 mb-3">
                                    <span className="text-5xl font-bold text-black/90">
                                        {idea.impact}
                                    </span>
                                    <span className="text-xl text-black/50 pb-2">/10</span>
                                </div>
                                <div className="h-3 bg-black/10 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-amber-500 rounded-full transition-all"
                                        style={{ width: `${idea.impact * 10}%` }}
                                    />
                                </div>
                                <span className="text-sm text-black/50 mt-3 block">
                                    {getScoreLabel(idea.impact)}
                                </span>
                            </div>

                            {/* Ease */}
                            <div className="bg-black/[0.02] rounded-xl p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <Gauge size={24} weight="fill" className="text-emerald-500" />
                                    <span className="text-lg font-medium text-black/70">
                                        Facilità di Implementazione
                                    </span>
                                </div>
                                <div className="flex items-end gap-3 mb-3">
                                    <span className="text-5xl font-bold text-black/90">
                                        {ease}
                                    </span>
                                    <span className="text-xl text-black/50 pb-2">/10</span>
                                </div>
                                <div className="h-3 bg-black/10 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-emerald-500 rounded-full transition-all"
                                        style={{ width: `${ease * 10}%` }}
                                    />
                                </div>
                                <span className="text-sm text-black/50 mt-3 block">
                                    {getScoreLabel(ease)}
                                </span>
                            </div>
                        </div>

                        {/* Description */}
                        {idea.description && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-black/70 mb-3">
                                    Descrizione
                                </h3>
                                <p className="text-black/70 text-lg leading-relaxed bg-black/[0.02] rounded-xl p-6">
                                    {idea.description}
                                </p>
                            </div>
                        )}

                        {/* Source Badge */}
                        <div className="flex items-center gap-2 pt-4 border-t border-black/5">
                            {isAIGenerated ? (
                                <>
                                    <Robot size={20} className="text-purple-500" />
                                    <span className="text-sm text-black/50">
                                        Generata da AI
                                    </span>
                                </>
                            ) : (
                                <>
                                    <User size={20} className="text-blue-500" />
                                    <span className="text-sm text-black/50">
                                        Aggiunta manualmente
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
